import {
  LayoutGrid,
  BarChart3,
  ReceiptText,
  Settings,
  Building2,
  UtensilsCrossed,
  Users,
  Store,
  Armchair,
  CalendarDays,
  MonitorPlay,
  type LucideIcon,
} from "lucide-react";
import type { ClubFeatureFlags } from "@/hooks/useClubFeatureFlags";
import type { Workspace } from "@/lib/workspaces";

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Shown but not clickable, with a reason (e.g. another organization's kiosk runs ordering here). */
  disabled?: boolean;
  disabledReason?: string;
}
/** @deprecated use NavItem */
export type ClubNavItem = NavItem;

/**
 * Tenant Portal navigation. Orders follows the tenant's own kiosk: shown when
 * the ordering flag is on, hidden when off (today's behaviour). When another
 * organization's kiosk serves the venue the item stays visible but disabled,
 * naming who runs ordering, so a club admin is never left guessing.
 */
export function getTenantNav(flags: ClubFeatureFlags, ws: Workspace | null): NavItem[] {
  const servedByOther = !!ws && !ws.has_own_operator;
  const orders: NavItem & { show: boolean } = servedByOther
    ? {
        label: "Orders",
        href: "/club/orders",
        icon: ReceiptText,
        show: true,
        disabled: true,
        disabledReason: ws.served_by_operator_name
          ? `Ordering at ${ws.stadium_name ?? "your venue"} is run by ${ws.served_by_operator_name}`
          : "No kiosk serves your venue yet",
      }
    : { label: "Orders", href: "/club/orders", icon: ReceiptText, show: flags.ordering };

  const items: (NavItem & { show: boolean })[] = [
    { label: "My Page", href: "/club", icon: LayoutGrid, show: true },
    { label: "My Stadium", href: "/club/stadium", icon: Building2, show: true },
    { label: "Screens", href: "/club/screens", icon: MonitorPlay, show: true },
    { label: "Insights", href: "/club/insights", icon: BarChart3, show: true },
    orders,
    { label: "Settings", href: "/club/settings", icon: Settings, show: true },
  ];
  return items.filter((i) => i.show).map(({ show, ...i }) => i);
}

/** Legacy entry point: the tenant nav with no workspace information. */
export function getClubNav(flags: ClubFeatureFlags): NavItem[] {
  return getTenantNav(flags, null);
}

/** Kiosk Portal navigation; staff work the order queue only (the database refuses them the rest). */
export function getKioskNav(role?: string | null): NavItem[] {
  const orders: NavItem = { label: "Orders", href: "/kiosk", icon: ReceiptText };
  if (role === "staff") return [orders];
  return [
    orders,
    { label: "Menu", href: "/kiosk/menu", icon: UtensilsCrossed },
    { label: "Reporting", href: "/kiosk/reporting", icon: BarChart3 },
    { label: "Settings", href: "/kiosk/settings", icon: Settings },
  ];
}

/** Stadium Portal navigation: aggregates across tenants, never their rows. */
export function getVenueNav(): NavItem[] {
  return [
    { label: "Overview", href: "/venue", icon: LayoutGrid },
    { label: "Tenants", href: "/venue/tenants", icon: Users },
    { label: "Kiosks", href: "/venue/kiosks", icon: Store },
    { label: "Seats & Tags", href: "/venue/seats", icon: Armchair },
    { label: "Events", href: "/venue/events", icon: CalendarDays },
    { label: "Settings", href: "/venue/settings", icon: Settings },
  ];
}
